"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

const SEGMENTS = 12;
const CX = 100;
const CY = 92;
const INNER_RADIUS = 52;
const OUTER_RADIUS = 68;
const TICK_INNER = 72;
const TICK_OUTER = 78;
const SEGMENT_WIDTH = 7;

type ServiceMetricGaugeProps = {
  label: string;
  level: number;
  tone?: "default" | "success";
  className?: string;
};

function segmentAngle(index: number) {
  if (SEGMENTS <= 1) {
    return -180;
  }

  // First segment at -180deg (left), last at 0deg (right) across the upper arc.
  return -180 + (index / (SEGMENTS - 1)) * 180;
}

export default function ServiceMetricGauge({
  label,
  level,
  tone = "default",
  className,
}: ServiceMetricGaugeProps) {
  const [animatedLevel, setAnimatedLevel] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setAnimatedLevel(level));
    return () => cancelAnimationFrame(frame);
  }, [level]);

  const activeCount = Math.round((animatedLevel / 100) * SEGMENTS);
  const activeColor =
    tone === "success" ? "var(--ok)" : "var(--foreground)";
  const inactiveColor =
    tone === "success"
      ? "color-mix(in srgb, var(--ok) 16%, transparent)"
      : "color-mix(in srgb, var(--foreground) 12%, transparent)";

  return (
    <div
      className={cn(
        "flex w-full flex-col items-center justify-center text-center",
        className,
      )}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={level}
    >
      <p className="text-sm font-semibold text-foreground">{label}</p>
      <div className="mt-4 flex w-full flex-1 flex-col justify-center">
        <div className="relative w-full">
          <svg
            viewBox="0 0 200 112"
            className="block h-auto w-full"
            preserveAspectRatio="xMidYMax meet"
            aria-hidden
          >
            {Array.from({ length: SEGMENTS }, (_, index) => {
              const angle = segmentAngle(index);
              const isActive = index < activeCount;
              const color = isActive ? activeColor : inactiveColor;

              return (
                <g
                  key={index}
                  transform={`rotate(${angle} ${CX} ${CY})`}
                  className="transition-opacity duration-500 ease-out"
                  style={{ opacity: isActive ? 1 : 0.85 }}
                >
                  <line
                    x1={CX + TICK_INNER}
                    y1={CY}
                    x2={CX + TICK_OUTER}
                    y2={CY}
                    stroke={color}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    className="transition-[stroke] duration-500 ease-out"
                  />
                  <rect
                    x={CX + INNER_RADIUS}
                    y={CY - SEGMENT_WIDTH / 2}
                    width={OUTER_RADIUS - INNER_RADIUS}
                    height={SEGMENT_WIDTH}
                    rx={SEGMENT_WIDTH / 2}
                    fill={color}
                    className="transition-[fill] duration-500 ease-out"
                  />
                </g>
              );
            })}
          </svg>

          <p className="pointer-events-none absolute inset-x-0 bottom-[14%] font-heading text-2xl font-black tracking-tight text-foreground sm:text-3xl">
            {level}%
          </p>
        </div>
      </div>
    </div>
  );
}
